import React, { useCallback, useEffect, useState } from "react";
import DefaultImage from "../../../img/app/default-Image.webp";
import moment from "moment-timezone";
import { toScreen } from "../../../js/modules/Routing";
import { useNavigate } from "react-router-dom";
import { defaultAvatarImage, imageFormat, jobImagesFolder, serverUrl, thumbSuffix, userImagesFolder } from "../../../config/env";
import { defaultMessagesFilter } from "../../../config/constants";
import {
  addJobNotes,
  driverCancellationPenalty,
  getJobNotes,
} from "../../../services/issueService";
import { closeSpinner, modal, showSpinner } from "../../../js/modules/Dialogs";

const DriverPenalty = (props) => {
  const {
    onClose,
    selectedIssueResolution,
    userDetails,
    theCallBackFunc,
    conversationDetails,
    getConversations,
  } = props;

  const [activeTab, setActiveTab] = useState("chat");
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isDecisionMade, setIsDecisionMade] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
  setIsDecisionMade(false);
}, [selectedIssueResolution]); // or modal open trigger


  const INITIAL_NOTES = [];

  const handleGetJobNotes = useCallback(async () => {
    if (!selectedIssueResolution?.jobDetails?._id) {
      setNotes(INITIAL_NOTES);
      return;
    }

    setIsLoading(true);
    showSpinner();

    const queryParams = {
      noteType: "job",
      id: selectedIssueResolution?.jobDetails?._id,
    };

    try {
      const resJson = await getJobNotes(queryParams);

      closeSpinner();
      setIsLoading(false);

      setNotes(resJson?.notes || INITIAL_NOTES);
    } catch (error) {
      closeSpinner();
      setIsLoading(false);
      if (error.response?.status === 400) {
        modal(error.message);
      } else {
        modal(error.message || "Failed to fetch job notes");
      }
      setNotes(INITIAL_NOTES);
    }
  }, [selectedIssueResolution?.jobDetails?._id]);

  // Add job notes with proper error handling
  const handleAddJobNotes = useCallback(
    async ({ id, content, noteType} ) => {
      const trimmedContent = content?.trim();
      if (trimmedContent) {
        showSpinner();

        const payload = {
          id: selectedIssueResolution?.jobDetails?._id,
          content: trimmedContent,
          noteType: "job",
        };

        try {
          const resJson = await addJobNotes(payload);

          closeSpinner();
          setIsAddingNote(false);

          modal("Note added successfully");
          setNoteInput("");
          handleGetJobNotes();
        } catch (error) {
          closeSpinner();
          setIsAddingNote(false);
          if (error.response?.status === 400) {
            modal(error.message);
          } else {
            modal(error.message || "Failed to add note");
          }
        }
      }
    },
    [selectedIssueResolution?.jobDetails?._id, theCallBackFunc, handleGetJobNotes]
  );


  const handleFetchDriverCancellationPenalty = async ({
    penaltyId,
    verdict,
  }) => {
    showSpinner();

    const payload = {
      penaltyId,
      verdict,
    };

    try {
      const resJson = await driverCancellationPenalty(payload);

      closeSpinner();

      onClose();
      theCallBackFunc();
      modal("Driver Penalty has been resolved");
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 400) {
        modal(error.message);
      } else {
        modal(error.message || "Failed to process driver penalty");
      }
    }
  };

  const getJobTypeDisplay = (jobType) => {
    const jobTypeMap = {
      getitems: "Get items",
      housemove: "House move",
      senditems: "Send items",
      wasteDisposal: "Waste Disposal",
    };
    return jobTypeMap[jobType] || jobType;
  };

  const cancellationRatePercentage =
    (selectedIssueResolution?.driverDetails?.cancellationRate /
      selectedIssueResolution?.driverDetails?.bookings) *
    100;

  const jobStatus = {
    paid: {
      label: "Paid",
      badge: "badge-success",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-circle-check-big lucide-icon h-3 w-3"
        >
          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
          <path d="m9 11 3 3L22 4"></path>
        </svg>
      ),
    },
    closed: {
      label: "Cancelled",
      badge: "badge-danger",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-x h-3 w-3"
        >
          <path d="M18 6 6 18"></path>
          <path d="m6 6 12 12"></path>
        </svg>
      ),
    },
    open: {
      label: "Open",
      badge: "badge-warning",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-circle-arrow-up lucide-icon h-3 w-3"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <path d="m16 12-4-4-4 4"></path>
          <path d="M12 16V8"></path>
        </svg>
      ),
    },
  };

  const jobDetailsStatus = selectedIssueResolution?.jobDetails?.status;
  const jobStatusData = jobStatus[jobDetailsStatus?.toLowerCase()];

  const statusMap = {
    exempt: {
      label: "Waived",
      badge: "badge-secondary",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-circle-off lucide-icon h-3 w-3"
        >
          <path d="m2 2 20 20"></path>
          <path d="M8.35 2.69A10 10 0 0 1 21.3 15.65"></path>
          <path d="M19.08 19.08A10 10 0 1 1 4.92 4.92"></path>
        </svg>
      ),
    },
    "not exempt": {
      label: "Applied",
      badge: "badge-danger",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-circle-arrow-up lucide-icon h-3 w-3"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <path d="m16 12-4-4-4 4"></path>
          <path d="M12 16V8"></path>
        </svg>
      ),
    },
    pending: {
      label: "Pending",
      badge: "badge-warning",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-clock h-3 w-3"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
    },
    approved: {
      label: "Approved",
      badge: "badge-success",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-check h-3 w-3"
        >
          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
          <path d="m9 11 3 3L22 4"></path>
        </svg>
      ),
    },
    rejected: {
      label: "Rejected",
      badge: "badge-danger",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-x h-3 w-3"
        >
          <path d="M18 6 6 18"></path>
          <path d="m6 6 12 12"></path>
        </svg>
      ),
    },
  };

  const status = selectedIssueResolution?.status;
  const statusData = statusMap[status?.toLowerCase()];

  const getDriverAvatarSrc = useCallback((selectedIssueResolution) => {
    if (
      selectedIssueResolution?.driverDetails?.avatar &&
      selectedIssueResolution?.driverDetails.avatar !== ""
    ) {
      return `${serverUrl}${userImagesFolder}/${selectedIssueResolution?.driverDetails?.shortId}/${selectedIssueResolution?.driverDetails?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const getUserAvatarSrc = useCallback((selectedIssueResolution) => {
    if (
      selectedIssueResolution?.userDetails?.avatar &&
      selectedIssueResolution?.userDetails?.avatar !== ""
    ) {
      return `${serverUrl}${userImagesFolder}/${selectedIssueResolution?.userDetails?.shortId}/${selectedIssueResolution?.userDetails?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const getJobDetailsSrc = useCallback((shortId, image) => {
    if (image) {
      return `${serverUrl}${jobImagesFolder}/${shortId}/${image}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${DefaultImage}${imageFormat}`;
  }, []);

  return (
    <div className="right-modal">
      {/* Modal Header */}
      <div className="modal-header">
        <div className="modal-title">
          <h2>Driver Penalty Details</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              className="lucide lucide-x h-5 w-5"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>
      </div>
      {/* Modal Body */}
      <div className="modal-body">
        {/* Booking Cancellation Info */}
        <div className="requested-info">
          <span className="request-name">
            Booking cancelled on{" "}
            {moment(
              selectedIssueResolution?.cancellationDetails?.cancelledOn
            ).format("DD-MM-YYYY")}{" "}
            at{" "}
            {moment(
              selectedIssueResolution?.cancellationDetails?.cancelledOn
            ).format("HH:mm")}
          </span>

          {statusData && (
            <span className={`badge text-capitalize ${statusData.badge}`}>
              {statusData.icon}
              <span className="ml-1">{statusData.label}</span>
            </span>
          )}
        </div>
        <div className="summary-box">
          <div>
            <span className="sum-title mb-4">
              Reason for driver cancellation
            </span>
            <span className="sum-description">Inaccurate job description</span>
          </div>
        </div>
        {/* Driver Info */}
        <div className="driver-section">
          <div className="profile-section">
            <div className="profile-info">
              <img
                src={getDriverAvatarSrc(selectedIssueResolution)}
                alt="avatar"
                className="avatar"
                onError={(e) => {
                  e.target.src = defaultAvatarImage;
                }}
              />
              <div>
                <h3 className="text-capitalize">
                  {selectedIssueResolution?.driverDetails?.firstname}
                </h3>
                <p>
                  Driver ID: {selectedIssueResolution?.driverDetails?.shortId}
                </p>
                {/* <p>
                  Member since:{" "}
                  {
                    selectedIssueResolution?.driverDetails?.accountInfo
                      ?.createdAt
                  }
                </p> */}
              </div>
            </div>
            <div className="profile-actions">
              <button
                onClick={(e) =>
                  toScreen(
                    e,
                    navigate,
                    "/mav/" + selectedIssueResolution?.driverDetails?.shortId
                  )
                }
              >
                {" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Profile
              </button>
              <button
                className="c-btn c-btn--small c-btn--full-width  c-btn--green"
                onClick={(e) =>
                  toScreen(
                    e,
                    navigate,
                    "/message/" +
                      selectedIssueResolution?.driverDetails?.shortId,
                    false,
                    {
                      receiverId: selectedIssueResolution?.driverDetails?._id,
                      receiverShortId:
                        selectedIssueResolution?.driverDetails?.shortId,
                      receiverType: "user",
                      userShortId1: "admin",
                      userShortId2:
                        selectedIssueResolution?.driverDetails?.shortId,
                      username2:
                        selectedIssueResolution?.driverDetails?.firstname,
                      selectedPage: window.history.state.usr?.selectedPage ?? 1,
                      selectedFilter:
                        window.history.state.usr?.selectedFilter ??
                        defaultMessagesFilter,
                      previousPath:
                        window.history.state.usr?.previousPath ??
                        window.location.pathname,
                    }
                  )
                }
              >
                {" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
                </svg>
                Chat
              </button>
            </div>
          </div>
          <div className="stats-section">
            <div className="stat-item">
              <div className="stat-label">Total Bookings</div>
              <div className="stat-value">
                {selectedIssueResolution?.driverDetails?.bookings}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Cancellations</div>
              <div className="stat-value">
                {selectedIssueResolution?.driverDetails?.cancellationRate} (
                {cancellationRatePercentage.toFixed(2)}%)
              </div>
            </div>
            {/* <div className="stat-item">
              <div className="stat-label">Driver Since</div>
              <div className="stat-value">{selectedIssueResolution?.driverDetails?.accountInfo?.createdAt}</div>
            </div> */}
          </div>
        </div>
        {/* Job Details */}
        <div className="job-section">
          <div className="section-header">
            <h3 className="section-title">Job Details</h3>
            <button
              className="btn btn-secondary"
              onClick={(e) =>
                toScreen(
                  e,
                  navigate,
                  "/job/" + selectedIssueResolution?.jobDetails?.shortId
                )
              }
            >
              View Job Details
            </button>
          </div>
          <div className="job-images">
            {selectedIssueResolution?.jobDetails?.images?.length > 1 &&
              selectedIssueResolution?.jobDetails?.images?.map(
                (image, index) => (
                  <img
                    key={index}
                    src={getJobDetailsSrc(
                      selectedIssueResolution?.jobDetails?.shortId,
                      image
                    )}
                    alt={`Job image ${index + 1}`}
                    className="c-avatar__pic"
                    onError={(e) => {
                      e.target.src = DefaultImage;
                    }}
                  />
                )
              )}
          </div>
          <div className="job-details"></div>
          <div className="job-info-row">
            <div className="job-info-col">
              <span className="job-info-label">Job Type</span>
              <span className="job-info-value">
                {getJobTypeDisplay(
                  selectedIssueResolution?.jobDetails?.jobType
                )}
              </span>
            </div>
            <div className="job-info-col">
              <span className="job-info-label">Job Status</span>
              {jobStatusData && (
                <span
                  className={`badge text-capitalize ${jobStatusData?.badge}`}
                >
                  {jobStatusData?.icon}
                  <span className="ml-1">{jobStatusData?.label}</span>
                </span>
              )}
            </div>
          </div>
          <div className="job-info-row">
            <div className="job-info-col">
              <span className="job-info-label">From</span>
              <span className="job-info-value">
                {selectedIssueResolution?.jobDetails?.from}
              </span>
            </div>
            <div className="job-info-col">
              <span className="job-info-label">To</span>
              <span className="job-info-value">
                {selectedIssueResolution?.jobDetails?.to}
              </span>
            </div>
          </div>
          <div className="job-info-row">
            <div className="job-info-col">
              <span className="job-info-label">Booked on</span>
              <span className="job-info-value">
                {moment(selectedIssueResolution?.jobDetails?.bookedOn).format(
                  "DD-MM-YYYY"
                )}
              </span>
            </div>
            <div className="job-info-col">
              <span className="job-info-label">Scheduled for</span>
              <span className="job-info-value">
                {moment(
                  selectedIssueResolution?.jobDetails?.scheduledFor
                ).format("DD-MM-YYYY")}
              </span>
            </div>
          </div>
          <div className="job-info-row">
            <div className="job-info-col">
              <span className="job-info-label">Cancelled on</span>
              <span className="job-info-value">
                {moment(
                  selectedIssueResolution?.jobDetails?.cancelledOn
                ).format("DD-MM-YYYY")}
              </span>
            </div>
            <div className="job-info-col">
              <span className="job-info-label">Booked for</span>
              <span className="job-info-value">
                {selectedIssueResolution?.jobDetails?.bookedFor || 0}
              </span>
            </div>
          </div>
        </div>
        {/* Customer Info */}
        <div className="driver-section">
          <h3 className="section-title mb-12">Customer</h3>
          <div className="profile-section mb-0">
            <div className="profile-info">
              <img
                src={getUserAvatarSrc(selectedIssueResolution)}
                alt="avatar"
                className="avatar"
                onError={(e) => {
                  e.target.src = defaultAvatarImage;
                }}
              />
              <div>
                <h3>{selectedIssueResolution?.userDetails?.firstname}</h3>
                <p>ID: {selectedIssueResolution?.userDetails?.shortId}</p>
              </div>
            </div>
            <div className="profile-actions">
              <button
                onClick={(e) =>
                  toScreen(
                    e,
                    navigate,
                    "/client/" + selectedIssueResolution?.userDetails?.shortId
                  )
                }
              >
                {" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Profile
              </button>
              <button
                className="c-btn c-btn--small c-btn--full-width  c-btn--green"
                onClick={(e) =>
                  toScreen(
                    e,
                    navigate,
                    "/message/" + selectedIssueResolution?.userDetails?.shortId,
                    false,
                    {
                      receiverId: selectedIssueResolution?.userDetails?._id,
                      receiverShortId:
                        selectedIssueResolution?.userDetails?.shortId,
                      receiverType: "user",
                      userShortId1: "admin",
                      userShortId2:
                        selectedIssueResolution?.userDetails?.shortId,
                      username2:
                        selectedIssueResolution?.userDetails?.firstname,
                      selectedPage: window.history.state.usr?.selectedPage ?? 1,
                      selectedFilter:
                        window.history.state.usr?.selectedFilter ??
                        defaultMessagesFilter,
                      previousPath:
                        window.history.state.usr?.previousPath ??
                        window.location.pathname,
                    }
                  )
                }
              >
                {" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
                </svg>
                Chat
              </button>
            </div>
          </div>
        </div>
        {/* Tabs Section */}
        <div className="chat-tabs">
          <div
            className={`chat-tab${activeTab === "chat" ? " active" : ""}`}
            onClick={() => {
              setActiveTab("chat");
              getConversations(selectedIssueResolution);
            }}
            tabIndex={0}
            role="button"
            aria-pressed={activeTab === "chat"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            Client-Driver Conversation
          </div>
          <div
            className={`chat-tab${activeTab === "notes" ? " active" : ""}`}
            onClick={() => {
              setActiveTab("notes");
              handleGetJobNotes();
            }}
            tabIndex={0}
            role="button"
            aria-pressed={activeTab === "notes"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
              <path d="M10 9H8"></path>
              <path d="M16 13H8"></path>
              <path d="M16 17H8"></path>
            </svg>
            Job Notes
          </div>
        </div>
        {/* Chat Tab */}
        {activeTab === "chat" && (
          <div className="chat-history">
            {conversationDetails?.messages?.map((messageDetails, index) => {
              const userType =
                messageDetails?.userType === "selectedIssueResolution"
                  ? "driver"
                  : "client";
              return (
                <div key={index} className={`chat-message ${userType}`}>
                  <div class="text-sm mb-4 text-capitalize">{userType}</div>
                  <div className="text">{messageDetails?.message}</div>
                  <div className="meta">
                    {messageDetails?.time} ,{messageDetails?.date}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Notes Tab */}
        {activeTab === "notes" && (
          <div className="job-notes">
            {notes?.map((note, idx) => (
              <div className="note-item" key={idx}>
                <div className="note-header">
                  <div className="author text-capitalize">{"Admin"}</div>
                  <div className="timestamp">
                    {" "}
                    {new Date(note?.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="note-body text-sm">{note?.content}</div>
              </div>
            ))}

            <div className="add-note">
              <label htmlFor="add-note-textarea">Add a note</label>
              <textarea
                id="add-note-textarea"
                placeholder="Type your note here..."
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                aria-label="Add note"
              ></textarea>
              <button
                className="btn add-btn"
                onClick={() =>
                  handleAddJobNotes({
                     id: selectedIssueResolution?._id,
                    noteType: "job",
                    content: noteInput,
                  })
                }
              >
                <strong>Add Note</strong>
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Modal Footer */}
      <div className="modal-footer">
        <button className="btn btn-outline-primary" onClick={onClose}>
          Close
        </button>
        {selectedIssueResolution?.status === "pending" && (
          <div>
            <button
              className="btn btn-danger"
              style={{ marginRight: 12 }}
              onClick={() => {
                setIsDecisionMade(true);

                handleFetchDriverCancellationPenalty({
                  penaltyId: selectedIssueResolution?._id,
                  verdict: "exempt",
                });
              }}
            >
              Waive Penalty
            </button>
            <button
              className="btn btn-success"
              onClick={() => {
                setIsDecisionMade(true);

                handleFetchDriverCancellationPenalty({
                  penaltyId: selectedIssueResolution?._id,
                  verdict: "not exempt",
                });
              }}
            >
              Apply Penalty
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverPenalty;
